import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Crosshair, Map, Gauge, Swords, Users } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { ModelPicker } from "@/components/common/ModelPicker";
import { SCOUT_ICON } from "@/lib/nav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

export default function Scout() {
    const [data, setData] = useState(null);
    const [games, setGames] = useState([]);
    const [game, setGame] = useState("warzone");
    const [model, setModel] = useState("gpt-5.2");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [s, g] = await Promise.all([api.get("/scout"), api.get("/games")]);
        setData(s.data); setGames(g.data.games);
        if (s.data.game) setGame(s.data.game);
    };
    useEffect(() => { load(); }, []);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/scout/generate", { game, model });
            setData(data);
            if (data.generation_status === "fallback") toast.warning(data.generation_note || "AI was unavailable; deterministic scout defaults were used.");
            else toast.success("Scout report updated.");
        }
        catch { toast.error("Generation failed."); }
        finally { setBusy(false); }
    };

    if (!data) return <Loader label="Loading AI scout" />;

    const cards = [
        { icon: Swords, label: "Best Weapon", value: data.best_weapon, color: "#00E676" },
        { icon: Swords, label: "Worst Weapon", value: data.worst_weapon, color: "#FF3366" },
        { icon: Map, label: "Best Map", value: data.best_map, color: "#00C2FF" },
        { icon: Map, label: "Worst Map", value: data.worst_map, color: "#FFB000" },
        { icon: Gauge, label: "Best Range", value: data.best_range, color: "#00C2FF" },
        { icon: Crosshair, label: "Best Playstyle", value: data.best_playstyle, color: "#00C2FF" },
        { icon: Users, label: "Best Role", value: data.best_role, color: "#00C2FF" },
    ];

    return (
        <div className="ww-fade-up">
            <PageHeader title="AI Scout" subtitle="Data-driven loadout, map and role recommendations.">
                <Select value={game} onValueChange={setGame}>
                    <SelectTrigger className="w-[170px] h-9 bg-surface border-border rounded-sm text-xs" data-testid="scout-game"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface2 border-border">
                        {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <ModelPicker value={model} onChange={setModel} />
                <button data-testid="generate-scout" onClick={generate} disabled={busy}
                    className="h-9 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2 disabled:opacity-60">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Scout
                </button>
            </PageHeader>
            {data.generation_status === "fallback" && (
                <div className="mb-4 rounded-sm border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
                    Deterministic scout defaults shown — the selected AI provider was unavailable.
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {cards.map((c) => (
                    <div key={c.label} className="bg-surface border border-border rounded-sm p-5" data-testid={`scout-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        <c.icon className="w-4 h-4 mb-2" style={{ color: c.color }} />
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                        <div className="font-heading font-bold text-white mt-1">{c.value}</div>
                    </div>
                ))}
            </div>

            <div className="bg-surface border border-border rounded-sm p-6 flex flex-col md:flex-row gap-6">
                <img src={SCOUT_ICON} alt="" className="w-20 h-20 object-contain shrink-0 opacity-90" />
                <div className="flex-1">
                    <h3 className="font-heading font-bold mb-3">Scout Recommendations</h3>
                    <ul className="space-y-2.5">
                        {data.recommendations?.map((r, i) => (
                            <li key={i} className="flex gap-3 text-sm text-foreground/90" data-testid={`scout-rec-${i}`}>
                                <span className="text-volt font-mono">▸</span>{r}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}
